// --- Dependencies --------------------------------------------------------------------------------
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import type { TyApiResponse, TyBranchDetail, TyBranchDetailPrice, TyProduct } from "@/types";
import useFetch from "@/hooks/useFetch";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import Skeleton from "@/UI/atoms/Skeleton";
import ActionsBar from "@/UI/components/ActionsBar";
import BranchProductCard from "@/UI/cards/BranchProductCard";
import MoreActionsPopup from "@/UI/popups/MoreActionsPopup";
import BranchProductSettingsPopup from "@/UI/popups/BranchProductSettingsPopup";
import type { BranchProductSettingsPopupDetails } from "@/UI/popups/BranchProductSettingsPopup";
import BranchProductDiffPopup from "@/UI/popups/BranchProductDiffPopup";
import type { BranchProductDiffPopupDetails } from "@/UI/popups/BranchProductDiffPopup";
import "./styles.css";

// --- Logic --------------------------------------------------------------------------------------

export default function BranchCategoryScreen() {
    const t = useTranslation();
    const navigate = useNavigate();
    const { addComp, removeComp } = usePopup();
    const { canEditBranch } = usePermission();
    const { id: branchId, categoryId } = useParams<{ id: string; categoryId: string }>();
    const location = useLocation();
    const categoryName = (location.state as { name?: string } | null)?.name ?? "";

    const {
        data: productsData,
        loading: productsLoading,
        error,
    } = useFetch<TyApiResponse<{ products: TyProduct[] }>>(`/menu/categories/${categoryId}/products`);
    const { data: branchData, loading: branchLoading } = useFetch<TyApiResponse<{ branch: TyBranchDetail }>>(`/branches/${branchId ?? "x"}`);

    const products = productsData?.data?.products ?? [];
    const branch = branchData?.data?.branch ?? null;

    const loading = productsLoading || branchLoading;

    // Local overlay state — updated optimistically on successful API calls
    const [inActive, setInActive] = useState<string[]>([]);
    const [hidden, setHidden] = useState<string[]>([]);
    const [branchPrices, setBranchPrices] = useState<TyBranchDetailPrice[]>([]);

    useEffect(() => {
        if (!branch) return;
        setInActive(branch.inActive ?? []);
        setHidden(branch.hidden ?? []);
        setBranchPrices(branch.prices ?? []);
    }, [branch]);

    // --- Toggle inactive at branch ---------------------------------------------------------------

    const [togglingInactiveId, setTogglingInactiveId] = useState<string | null>(null);

    const { data: toggleInactiveData, invoke: invokeToggleInactive } = useFetch<TyApiResponse<unknown>>(
        `/branches/products/${togglingInactiveId ?? "x"}`,
        { method: "PUT" },
        false
    );

    useEffect(() => {
        if (!togglingInactiveId) return;
        const isCurrentlyInactive = inActive.includes(togglingInactiveId);
        invokeToggleInactive({ method: "PUT", data: { branchId, active: isCurrentlyInactive } });
    }, [togglingInactiveId]);

    useEffect(() => {
        if (!toggleInactiveData?.success || !togglingInactiveId) return;
        const toggledId = togglingInactiveId;
        setInActive((prev) => (prev.includes(toggledId) ? prev.filter((id) => id !== toggledId) : [...prev, toggledId]));
        setTogglingInactiveId(null);
    }, [toggleInactiveData]);

    // --- Reset product to default ----------------------------------------------------------------

    const [resettingProductId, setResettingProductId] = useState<string | null>(null);

    const { data: resetData, invoke: invokeReset } = useFetch<TyApiResponse<unknown>>(
        `/branches/products/${resettingProductId ?? "x"}`,
        { method: "PUT" },
        false
    );

    useEffect(() => {
        if (!resettingProductId) return;
        invokeReset({ data: { branchId, reset: true } });
    }, [resettingProductId]);

    useEffect(() => {
        if (!resetData?.success || !resettingProductId) return;
        const productId = resettingProductId;
        setInActive((prev) => prev.filter((id) => id !== productId));
        setHidden((prev) => prev.filter((id) => id !== productId));
        setBranchPrices((prev) => prev.filter((bp) => bp.product !== productId));
        setResettingProductId(null);
    }, [resetData]);

    // --- Toggle hidden at branch -----------------------------------------------------------------

    const [togglingHiddenId, setTogglingHiddenId] = useState<string | null>(null);

    const { data: toggleHiddenData, invoke: invokeToggleHidden } = useFetch<TyApiResponse<unknown>>(
        `/branches/products/${togglingHiddenId ?? "x"}`,
        { method: "PUT" },
        false
    );

    useEffect(() => {
        if (!togglingHiddenId) return;
        const isCurrentlyHidden = hidden.includes(togglingHiddenId);
        invokeToggleHidden({ method: "PUT", data: { branchId, hidden: !isCurrentlyHidden } });
    }, [togglingHiddenId]);

    useEffect(() => {
        if (!toggleHiddenData?.success || !togglingHiddenId) return;
        const toggledId = togglingHiddenId;
        setHidden((prev) => (prev.includes(toggledId) ? prev.filter((id) => id !== toggledId) : [...prev, toggledId]));
        setTogglingHiddenId(null);
    }, [toggleHiddenData]);

    // --- Handlers --------------------------------------------------------------------------------

    const handleEditPrices = (product: TyProduct) => {
        const existingEntry = branchPrices.find((bp) => bp.product === product._id) ?? null;
        const details: BranchProductSettingsPopupDetails = {
            productId: product._id,
            productName: product.name,
            branchId: branchId ?? "",
            existingEntry,
            onSuccess: ({ prices, hiddenPrices, discount }) => {
                setBranchPrices((prev) => {
                    const exists = prev.find((bp) => bp.product === product._id);
                    if (exists) return prev.map((bp) => (bp.product === product._id ? { ...bp, prices, hiddenPrices, discount } : bp));
                    return [...prev, { product: product._id, prices, hiddenPrices, discount }];
                });
            },
        };
        addComp({
            name: `edit-branch-product-${product._id}`,
            Component: BranchProductSettingsPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details,
        });
    };

    const handleViewDiff = (product: TyProduct) => {
        const branchEntry = branchPrices.find((bp) => bp.product === product._id) ?? null;
        const details: BranchProductDiffPopupDetails = {
            product,
            branchEntry,
            isInactiveAtBranch: inActive.includes(product._id),
            isHiddenAtBranch: hidden.includes(product._id),
        };
        addComp({
            name: `diff-popup-${product._id}`,
            Component: BranchProductDiffPopup,
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
            details,
        });
    };

    const handleMore = (product: TyProduct, e: React.MouseEvent<HTMLButtonElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const name = `more-actions-${product._id}`;
        const branchEntry = branchPrices.find((bp) => bp.product === product._id) ?? null;
        const hasOverrides =
            inActive.includes(product._id) ||
            hidden.includes(product._id) ||
            (branchEntry !== null && (branchEntry.prices.length > 0 || branchEntry.hiddenPrices.length > 0 || branchEntry.discount !== null));
        removeComp(name);
        addComp({
            name,
            Component: MoreActionsPopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            coverClose: true,
            anchor: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height },
            details: {
                actions: [
                    { label: t.branchMenu.editForBranchLabel, icon: "/icons/edit-icon.svg", handler: () => handleEditPrices(product) },
                    {
                        label: t.branchMenu.viewChangesLabel,
                        icon: "/icons/eye-opened-icon.svg",
                        handler: () => handleViewDiff(product),
                        disabled: !hasOverrides,
                    },
                    {
                        label: t.branchMenu.resetProductLabel,
                        icon: "/icons/clear-icon.svg",
                        handler: () => setResettingProductId(product._id),
                        disabled: !hasOverrides,
                    },
                ],
            },
        });
    };

    // --- Render ----------------------------------------------------------------------------------

    return (
        <div className="branch-category-screen">
            <ActionsBar title={categoryName} onBack={() => navigate(`/branches/${branchId}`)} />

            <div className="branch-category-screen-content">
                {loading && (
                    <div className="branch-category-screen-grid">
                        {Array.from({ length: 4 }).map((_, i) => (
                            <Skeleton key={i} className="branch-category-screen-skeleton" />
                        ))}
                    </div>
                )}

                {!loading && error && <p className="branch-category-screen-message branch-category-screen-message--error">{error}</p>}

                {!loading && !error && products.length === 0 && (
                    <div className="branch-category-screen-empty">
                        <div className="branch-category-screen-empty-icon">
                            <img src="/icons/price-icon.svg" alt="" width={32} height={32} />
                        </div>
                        <div className="branch-category-screen-empty-text">
                            <p className="branch-category-screen-empty-title">{t.menu.emptyProductsTitle}</p>
                            <p className="branch-category-screen-empty-subtitle">{t.menu.emptyProductsSubtitle}</p>
                        </div>
                    </div>
                )}

                {!loading && !error && products.length > 0 && (
                    <div className="branch-category-screen-grid">
                        {products.map((product) => (
                            <BranchProductCard
                                key={product._id}
                                product={product}
                                branchEntry={branchPrices.find((bp) => bp.product === product._id) ?? null}
                                isInactiveAtBranch={inActive.includes(product._id)}
                                isHiddenAtBranch={hidden.includes(product._id)}
                                canEdit={canEditBranch(branchId ?? "")}
                                onToggleInactive={
                                    canEditBranch(branchId ?? "")
                                        ? () => {
                                              if (togglingInactiveId) return;
                                              setTogglingInactiveId(product._id);
                                          }
                                        : undefined
                                }
                                onToggleHidden={
                                    canEditBranch(branchId ?? "")
                                        ? () => {
                                              if (togglingHiddenId) return;
                                              setTogglingHiddenId(product._id);
                                          }
                                        : undefined
                                }
                                togglingInactive={togglingInactiveId === product._id}
                                togglingHidden={togglingHiddenId === product._id}
                                onMore={canEditBranch(branchId ?? "") ? (e) => handleMore(product, e) : undefined}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
